'use client';

import classNames from 'classnames';
import { FiChevronDown } from 'react-icons/fi';

import BlogMenu from '~/shared/components/header/components/blog-menu/blog-menu';
import useMenu from '~/shared/components/header/components/menu/use-menu';
import PopularCourses from '~/shared/components/header/components/popular-courses/popular-courses';
import StudentsAttentionBadge from '~/shared/components/students-attention-badge';
import Link from '~/shared/components/link';
import { PAGE_NAMES } from '~/shared/constants';
import { VIEW_MODE, type ViewMode } from '~/shared/constants/general';
import { getLocaleLink, isItemSelected } from '~/shared/helpers/browser-helpers';
import type { Locale } from '~/universal/constants';

import './menu.scss';

type MenuProps = Readonly<{
  lng: Locale;
  viewMode?: ViewMode;
  user?: { role?: string | null } | null;
  isClientsPanel?: boolean;
  isAdminPanel?: boolean;
  isPlatform?: boolean;
  onMobileMenuClose?: () => void;
  withStudentsAttentionBadge?: boolean;
}>;

const Menu = ({
  user,
  isAdminPanel,
  isPlatform,
  onMobileMenuClose,
  withStudentsAttentionBadge = false,
  lng,
  viewMode = VIEW_MODE.HORIZONTAL,
}: MenuProps) => {
  const { isMounted, isClientsPanel, headerHeight, shellRef, pathname, openPopup, closeDropdown, items, setOpenPopup } =
    useMenu({
      isAdminPanel,
      isPlatform,
      onMobileMenuClose,
      user,
      lng,
    });

  if (!isMounted) {
    return <div ref={shellRef} className="menu" />;
  }

  return (
    <div
      ref={shellRef}
      className={classNames('menu', {
        'menu--open': openPopup,
        'menu--vertical': viewMode === VIEW_MODE.VERTICAL,
      })}
      onMouseLeave={closeDropdown}
    >
      <nav aria-label="Main navigation">
        <ul
          className={classNames('menu__list', {
            'menu__list--vertical': viewMode === VIEW_MODE.VERTICAL,
          })}
        >
          {items.map((item, i) => {
            const studentsBoardHref = getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_BOARD, lng);
            const isStudentsNavigationItem =
              item.href === studentsBoardHref ||
              item.key === studentsBoardHref ||
              item.activePaths?.includes(studentsBoardHref) ||
              item.href?.includes('/platform/students') ||
              item.key?.includes('/platform/students');

            if (item.popup) {
              const isActive = openPopup === item.popup;

              return (
                <li
                  key={item.key}
                  className={classNames('menu__item', 'menu__item--trigger', {
                    'menu__item--active': isActive,
                    'menu__item--selected': isItemSelected(item, pathname),
                    'menu__item--with-arrow': viewMode === VIEW_MODE.HORIZONTAL,
                  })}
                  onMouseEnter={() => (viewMode === VIEW_MODE.HORIZONTAL ? setOpenPopup(item.popup!) : null)}
                >
                  <Link
                    onClick={() => {
                      closeDropdown();
                      onMobileMenuClose?.();
                    }}
                    href={item.href ?? '#'}
                    className={classNames('menu__link', 'menu__link--trigger')}
                  >
                    <span>{item.label}</span>
                    {viewMode === VIEW_MODE.HORIZONTAL && (
                      <span className="menu__arrow" aria-hidden="true">
                        <FiChevronDown />
                      </span>
                    )}
                  </Link>
                </li>
              );
            }

            return (
              <li
                key={`${item.href ?? item.key}-${i}`}
                className={classNames('menu__item', {
                  'menu__item--selected': isItemSelected(item, pathname),
                })}
              >
                <Link
                  onClick={() => {
                    closeDropdown();
                    onMobileMenuClose?.();
                  }}
                  href={item.href ?? '#'}
                  className="menu__link"
                >
                  {withStudentsAttentionBadge && isStudentsNavigationItem ? (
                    <span className="menu__students-attention-label">
                      <span>{item.label}</span>
                      <StudentsAttentionBadge />
                    </span>
                  ) : (
                    item.label
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      {openPopup && (
        <div
          className="menu__dropdown"
          style={{
            top: `${headerHeight}px`,
          }}
        >
          <div className="menu__dropdown-inner">
            {openPopup === 'blog' && (
              <BlogMenu
                closeDropdown={() => {
                  closeDropdown();
                  onMobileMenuClose?.();
                }}
                lng={lng}
              />
            )}
            {openPopup === 'courses' && (
              <PopularCourses
                lng={lng}
                closeDropdown={() => {
                  closeDropdown();
                  onMobileMenuClose?.();
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Menu;

